import { create } from 'zustand';
import api from '@/utils/api';
import { Course, CourseItem, CourseModule } from '@/lib/types';
import { unwrap, CourseDraft, CourseItemDraft, itemsPayload } from '@/pages/courses/api';

interface ModuleFields {
  name: string;
  instructions: string;
  assignment_text: string;
  assignment_shortcut: string;
}

interface CoursesStore {
  items: Course[];
  // Workspace the items were loaded for; guards against a stale list showing
  // through after a workspace switch.
  loadedWorkspaceId: string | null;
  error: string | null;
  isLoading: boolean;
  loadCourses: (workspaceId: string) => Promise<void>;
  fetchCourse: (id: number) => Promise<Course | null>;
  saveCourse: (draft: CourseDraft) => Promise<Course>;
  deleteCourse: (id: number) => Promise<void>;
  // Publishing cuts a version annotated with the note — on a live course too,
  // which is how the next version is recorded without unpublishing.
  setCourseStatus: (courseId: number, status: Course['status'], note?: string) => Promise<Course>;
  createModule: (courseId: number, fields: ModuleFields) => Promise<CourseModule>;
  updateModule: (moduleId: number, fields: ModuleFields) => Promise<CourseModule>;
  deleteModule: (moduleId: number) => Promise<void>;
  sortModules: (courseId: number, moduleIds: number[]) => Promise<CourseModule[]>;
  saveModuleItems: (moduleId: number, items: CourseItemDraft[]) => Promise<CourseItem[]>;
}

const useCoursesStore = create<CoursesStore>()((set, get) => ({
  items: [],
  loadedWorkspaceId: null,
  error: null,
  isLoading: false,

  loadCourses: async (workspaceId) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get(`courses/workspace/${workspaceId}`);
      const courses = unwrap<Course[]>(response) ?? [];
      set({ items: courses, loadedWorkspaceId: workspaceId, isLoading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.error || 'Failed to load courses',
        isLoading: false,
      });
    }
  },

  fetchCourse: async (id) => {
    try {
      const response = await api.get(`courses/${id}`);
      return unwrap<Course>(response) ?? null;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      set({ error: error.response?.data?.error || 'Failed to load course' });
      throw error;
    }
  },

  saveCourse: async (draft) => {
    try {
      set({ error: null });
      const response = draft.id
        ? await api.put(`courses/${draft.id}`, draft)
        : await api.post('courses', draft);
      const saved = unwrap<Course>(response);
      const items = get().items;
      const exists = items.some((c) => c.id === saved.id);
      set({
        items: exists
          ? items.map((c) => (c.id === saved.id ? { ...c, ...saved } : c))
          : [saved, ...items],
      });
      return saved;
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to save course' });
      throw error;
    }
  },

  deleteCourse: async (id) => {
    try {
      set({ error: null });
      await api.delete(`courses/${id}`);
      set({ items: get().items.filter((c) => c.id !== id) });
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to delete course' });
      throw error;
    }
  },

  setCourseStatus: async (courseId, status, note = '') => {
    const response = await api.patch(`courses/${courseId}/status`, { status, note });
    const saved = unwrap<Course>(response);
    set({ items: get().items.map((c) => (c.id === saved.id ? { ...c, ...saved } : c)) });
    return saved;
  },

  createModule: async (courseId, fields) => {
    const response = await api.post(`courses/${courseId}/modules`, fields);
    return unwrap<CourseModule>(response);
  },

  updateModule: async (moduleId, fields) => {
    const response = await api.put(`courses/modules/${moduleId}`, fields);
    return unwrap<CourseModule>(response);
  },

  deleteModule: async (moduleId) => {
    await api.delete(`courses/modules/${moduleId}`);
  },

  sortModules: async (courseId, moduleIds) => {
    const response = await api.put(`courses/${courseId}/modules/sort-order`, {
      module_ids: moduleIds,
    });
    return unwrap<CourseModule[]>(response);
  },

  saveModuleItems: async (moduleId, items) => {
    const response = await api.put(`courses/modules/${moduleId}/items`, itemsPayload(items));
    return unwrap<CourseItem[]>(response);
  },
}));

export default useCoursesStore;
